import React, { useEffect, useState } from "react";

import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import { graphql } from "@/gql";
import { UpdateCoachProfileInput } from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import {
  muiError,
  muiField,
  useUpdateableFieldArray,
  valueAsNumber,
} from "@/utils/hookForm";

import { trainingsAndSpecializationQuery } from "./TrainingsAndSpecialization";

interface EditModalProps {
  onOpen?: boolean;
  onClose: () => void;
  modalType: string;
  data?: Datum[];
}

interface Datum {
  id?: string;
  name: string;
  year: number;
  _destroy?: boolean;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    overflowY: "auto",
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(24, 24),
    backgroundColor: colors.white,
    overflowY: "auto",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: theme.spacing(6, 8, 0, 8),
  },
  modalHeaderText: {
    fontSize: "24px",
    fontWeight: "bold",
  },
  modalContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(12),
  },
  formBody: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    justifyContent: "center",
    padding: theme.spacing(2, 8),
    marginTop: "12px",
  },
  fields: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "center",
    marginTop: "12px",
  },
  save: {
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    marginBottom: "24px",
    marginTop: "28px",
  },
}));

const StyledButton = withStyles(Button, (theme) => ({
  root: {
    height: "56px",
    backgroundColor: "#F7F9FB",
    borderRadius: 0,
    border: "1px solid #EDF0F5",
    "&:hover": {
      backgroundColor: "#F7F9FBaa",
    },
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(2),
  },
  label: {
    color: colors.grey,
  },
}));
const StyledTextField = withStyles(TextField, (theme) => ({
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F7F9FB",
    },
    width: "100%",
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));
const StyledSaveButton = withStyles(Button, {
  root: {
    backgroundColor: colors.grey,
    width: "132px",
    height: "52px",
    "&:hover": {
      backgroundColor: colors.grey,
    },
  },
});

const validationSchema = yup
  .object({
    data: yup
      .array()
      .of(
        yup
          .object({
            id: yup.string().optional(),
            name: yup.string().label("Name").required(),
            year: yup
              .number()
              .label("Year")
              .presence()
              .required()
              .max(new Date().getFullYear()),
            _destroy: yup.boolean().optional(),
          })
          .defined()
      )
      .defined(),
  })
  .defined();

type FormValues = yup.InferType<typeof validationSchema>;

const updateCoachProfileMutation = graphql(`
  mutation UpdateCoachProfile($input: UpdateCoachProfileInput!) {
    updateCoachProfile(input: $input) {
      coach {
        id
        coachProfile {
          id
          educations {
            id
            name
          }
          trainings {
            id
            name
          }
          certifications {
            id
            name
          }
        }
      }
    }
  }
`);

const EditModal: React.FC<EditModalProps> = ({
  onOpen,
  onClose,
  data,
  modalType,
}) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const modalVisibility = onOpen !== undefined ? onOpen : false;
  const [name, setName] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [updateCoachProfile] = useMutation(updateCoachProfileMutation);

  const { handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: {
      data: data,
    },
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const { fields, remove, prepend } = useUpdateableFieldArray({
    control,
    name: "data",
  });
  useEffect(() => {
    if (data) {
      reset({ data: data });
    }
  }, [data]);
  const onSubmit = async (formValues: FormValues) => {
    const values = validationSchema.cast(formValues);
    const input: UpdateCoachProfileInput = {
      patch: {
        trainingsAttributes: [],
        educationsAttributes: [],
        certificationsAttributes: [],
      },
    };
    switch (modalType) {
      case "education":
        input.patch.educationsAttributes = values.data.map((e) => ({
          id: e.id,
          name: e.name,
          year: e.year,
          _destroy: e._destroy,
        }));
        break;
      case "trainings":
        input.patch.trainingsAttributes = values.data.map((t) => ({
          id: t.id,
          name: t.name,
          year: t.year,
          _destroy: t._destroy,
        }));
        break;
      case "certification":
        input.patch.certificationsAttributes = values.data.map((c) => ({
          id: c.id,
          name: c.name,
          year: c.year,
          _destroy: c._destroy,
        }));
        break;
    }
    try {
      await updateCoachProfile({
        variables: { input: input },
        refetchQueries: [trainingsAndSpecializationQuery],
      });
      snackbar.open(`Your ${modalType} details has been updated`);
      onClose();
    } catch (e) {
      snackbar.open(
        `An error occured on updating your ${modalType} details. Please try again later`
      );
    }
  };

  return (
    <Modal
      open={modalVisibility}
      onClose={onClose}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      className={classes.root}
    >
      <Box className={classes.modalBody}>
        <Box className={classes.modalHeader}>
          <Typography className={classes.modalHeaderText}>
            Edit {modalType} details
          </Typography>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box className={classes.formBody}>
              <div className={classes.fields}>
                <StyledTextField
                  name="name"
                  fullWidth
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  helperText=" " // for spacing purposes only
                />
                <StyledTextField
                  name="year"
                  placeholder="Year"
                  type="number"
                  fullWidth
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  helperText=" " // for spacing purposes only
                />
                <StyledButton
                  onClick={() => {
                    prepend({ name: name, year: valueAsNumber(year) });
                    setName("");
                    setYear("");
                  }}
                >
                  <AddIcon />
                </StyledButton>
              </div>
              <Box>
                {fields.map((data, index) => {
                  return (
                    <React.Fragment key={data.key}>
                      <div className={classes.fields}>
                        <Controller
                          control={control}
                          name={`data.${index}.name` as const}
                          render={({ field, fieldState }) => (
                            <StyledTextField
                              {...muiField(field)}
                              {...muiError(fieldState)}
                              placeholder="Name"
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name={`data.${index}.year` as const}
                          render={({ field, fieldState }) => (
                            <StyledTextField
                              {...muiField(field)}
                              {...muiError(fieldState)}
                              placeholder="Year"
                              type="number"
                            />
                          )}
                        />

                        <StyledButton onClick={() => remove(index)}>
                          <RemoveIcon />
                        </StyledButton>
                      </div>
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
            <div className={classes.save}>
              <StyledSaveButton type="submit">Save</StyledSaveButton>
            </div>
          </form>
        </div>
      </Box>
    </Modal>
  );
};

export default EditModal;
